/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import { WafConfig } from 'object-types';
import { IJsonParsingLimits, IWafConfig } from 'generated-types';
import { MessageItem, RepeatedMessageItem } from 'ajs/modules/data-model/factories';
import { WafContentTypeMappingConfigItem } from 'ajs/modules/waf/factories/waf-profile';
import { withEditChildMessageItemMixin } from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { WafMatchElementSubParserConfigItem } from './waf-match-element-sub-parser.config-item.factory';

/**
 * Partial type of WafConfig.
 */
type TWafConfigPartial = Omit<IWafConfig,
'json_parsing_limits' |
'content_type_mappings' |
'match_element_sub_parsers'
>;

/**
 * WafConfig config type with message items.
 */
interface IWafConfigConfig extends TWafConfigPartial {
    json_parsing_limits: MessageItem<IJsonParsingLimits>;
    content_type_mappings: RepeatedMessageItem<WafContentTypeMappingConfigItem>;
    match_element_sub_parsers: RepeatedMessageItem<WafMatchElementSubParserConfigItem>;
}

/**
 * @description WafConfig Config Item
 * @author alextsg
 */
export class WafConfigConfigItem extends withEditChildMessageItemMixin(MessageItem<IWafConfigConfig>) {
    public getConfig: () => this['data']['config'];

    constructor(args = {}) {
        const extendedArgs = {
            objectType: WafConfig,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * @override
     */
    public modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        if (this.config.enable_json_parsing_limits) {
            this.safeSetNewChildByField('json_parsing_limits');
        }
    }

    /**
     * Handle toggle of enable_json_parsing_limits.
     */
    public onEnableJsonParsingLimitsChange(): void {
        if (this.config.enable_json_parsing_limits) {
            this.safeSetNewChildByField('json_parsing_limits');
        } else {
            delete this.config.json_parsing_limits;
        }
    }

    public addMatchElementSubParser(): void {
        this.addChildMessageItem({
            field: 'match_element_sub_parsers',
        });
    }

    public editMatchElementSubParser(subParser: WafMatchElementSubParserConfigItem): void {
        this.addChildMessageItem({
            field: 'match_element_sub_parsers',
            messageItem: subParser,
        });
    }
}

